import React, { useEffect, useState } from 'react';
import { Offcanvas, Form, Button, Spinner, Table, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import API_BASE_URL from '../../../config/api';
import { openAuthenticatedFile } from '../../../services/auth';
import { Box, Divider, Typography } from "@mui/material";
import {
  FileEarmarkTextFill,
  FilePdfFill,
  FileImageFill,
  JournalText,
  TrashFill,
  EyeFill,
  PlusCircleFill
} from "react-bootstrap-icons";

// Reusable SectionHeader component
const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

// Reusable field style
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  padding: "10px",
};

const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const AddNotesOffcanvas = ({ show, handleClose, classId }) => {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [error, setError] = useState('');

  const fetchNotes = async () => {
    if (!classId) return;
    
    try {
      setLoadingNotes(true);
      setError('');
      
      try {
        const response = await axios.get(`${API_BASE_URL}/api/notes/getNoteById/${classId}`);
        
        if (response.data.success) {
          setNotes(response.data.data ? [response.data.data] : []);
        } else if (response.data.data) {
          setNotes([response.data.data]);
        } else {
          setNotes([]);
        }
        
        return;
      } catch (apiError) {
        console.log('Specific endpoint failed, trying fallback');
      }
      
      const allNotesResponse = await axios.get(`${API_BASE_URL}/api/notes/getAllNotes`);
      
      if (allNotesResponse.data.success) {
        const classNotes = allNotesResponse.data.data.filter(note => {
          return note.classId === classId || 
                 note.classId?._id === classId || 
                 note.classDetails?._id === classId;
        });
        setNotes(classNotes);
      } else {
        setNotes([]);
        setError('No notes found for this class');
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      setError('Failed to load notes');
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (show && classId) {
      fetchNotes();
      reset();
    } else {
      setNotes([]);
      setError('');
    }
  }, [show, classId]);

  const onSubmit = async (data) => {
    if (!classId) {
      setError('No class selected');
      return;
    }
    
    try {
      setError('');
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('classId', classId);
      
      if (data.file && data.file[0]) {
        formData.append('uploadFile', data.file[0]);
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/notes/addNotes/${classId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (response.data.success) {
        console.log('Notes added successfully:', response.data);
        reset();
        fetchNotes();
      } else {
        setError('Failed to add notes: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding notes:', error);
      setError('Failed to add notes. Please try again.');
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FileEarmarkTextFill size={16} />;
    if (filename.toLowerCase().includes('.pdf')) {
      return <FilePdfFill size={16} color="#dc3545" />;
    }
    if (filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return <FileImageFill size={16} color="#0d47a1" />;
    }
    return <FileEarmarkTextFill size={16} color="#6c757d" />;
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 550 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <JournalText size={13} color="#ffeb3b" />
          </Box>
          Add Notes / Instructions
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff", overflowY: 'auto' }}>
        {error && (
          <Alert 
            variant="danger" 
            className="mb-3"
            style={{ 
              borderRadius: "8px", 
              border: "1.5px solid #f5c2c7",
              fontSize: "0.875rem"
            }}
          >
            {error}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit(onSubmit)} className="p-3 rounded-3 shadow-sm bg-white mb-4">
          
          <SectionHeader
            icon={<PlusCircleFill size={13} />}
            label="Add New Notes"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control 
              type="text"
              {...register('title', { required: 'Title is required' })}
              placeholder="Enter notes title"
              style={fieldStyle}
              isInvalid={!!errors.title}
            />
            {errors.title && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.title.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Upload File (PDF / Image)</Form.Label>
            <Form.Control 
              type="file" 
              accept="application/pdf,image/*" 
              {...register('file')}
              style={fieldStyle}
            />
            <Form.Text muted style={{ fontSize: "0.75rem", color: "#7b8ab8" }}>
              Accepted formats: PDF, JPG, PNG, GIF (max 10MB)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={labelStyle}>
              Description <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register('description', { required: 'Description is required' })}
              placeholder="Enter detailed description"
              style={fieldStyle}
              isInvalid={!!errors.description}
            />
            {errors.description && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.description.message}
              </div>
            )}
          </Form.Group>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
            style={{
              background: "linear-gradient(135deg, #1a237e, #0d47a1)",
              border: "none",
              borderRadius: "10px",
              padding: "11px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" animation="border" /> Saving Notes...
              </>
            ) : (
              <>
                <JournalText size={15} /> Save Notes
              </>
            )}
          </Button>
        </Form>

        <div className="mt-4">
          <SectionHeader
            icon={<FileEarmarkTextFill size={13} />}
            label={`Existing Notes (${notes.length})`}
          />

          {loadingNotes ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <Spinner animation="border" style={{ color: "#0d47a1" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "#7b8ab8" }}>
                Loading notes...
              </Typography>
            </Box>
          ) : notes.length > 0 ? (
            <div className="notes-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
              {notes.map((note, index) => (
                <Box
                  key={note._id || index}
                  className="mb-3 p-3"
                  sx={{
                    background: "#fff",
                    borderRadius: "8px",
                    border: "1.5px solid #dde4f5",
                    transition: "all 0.2s",
                    '&:hover': {
                      borderColor: "#0d47a1",
                      boxShadow: "0 2px 8px rgba(13,71,161,0.1)"
                    }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "6px",
                          background: "rgba(13,71,161,0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getFileIcon(note.uploadFile)}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1a237e" }}>
                          {note.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                          Added on {new Date().toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                      {note.uploadFile && (
                        <Button
                          variant="link"
                          onClick={() =>
                            openAuthenticatedFile(note.uploadFile).catch((error) =>
                              setError(error.message || 'Unable to open this attachment.')
                            )
                          }
                          className="p-0"
                          style={{ color: "#0d47a1" }}
                          title="View File"
                        >
                          <EyeFill size={16} />
                        </Button>
                      )}
                      {/* <Button
                        variant="link"
                        className="p-0"
                        style={{ color: "#dc3545" }}
                        onClick={() => handleDeleteNote(note._id)}
                        title="Delete Note"
                      >
                        <TrashFill size={16} />
                      </Button> */}
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#1a237e", mb: 2, fontSize: "0.875rem" }}>
                    {note.description}
                  </Typography>
                  {note.uploadFile && (
                    <Box
                      sx={{
                        background: "#f0f4ff",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "0.75rem",
                        color: "#0d47a1",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1
                      }}
                    >
                      {getFileIcon(note.uploadFile)}
                      <span style={{ fontWeight: 500 }}>
                        {note.uploadFile.split('/').pop() || 'Attachment'}
                      </span>
                    </Box>
                  )}
                </Box>
              ))}
            </div>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={4}
              sx={{ background: "#fff", borderRadius: "8px", border: "1.5px solid #dde4f5" }}
            >
              <FileEarmarkTextFill size={30} color="#dde4f5" />
              <Typography variant="body2" sx={{ mt: 2, color: "#7b8ab8" }}>
                No notes available for this class
              </Typography>
            </Box>
          )}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddNotesOffcanvas;
